import type { ComponentType, ReactNode } from 'react';

import { cn } from '@/lib/utils';

export type StateTone = 'neutral' | 'informative' | 'warning' | 'destructive';

const TONE_CLASSES: Record<StateTone, { icon: string; ring: string }> = {
  neutral: { icon: 'text-muted-foreground', ring: 'bg-muted' },
  informative: { icon: 'text-primary', ring: 'bg-accent' },
  warning: { icon: 'text-warning', ring: 'bg-warning/15' },
  destructive: { icon: 'text-destructive', ring: 'bg-destructive/15' },
};

export interface StateBlockProps {
  icon: ComponentType<{ className?: string }>;
  title: string;
  /** One sentence naming the cause. Never "something went wrong". */
  detail: string;
  tone?: StateTone;
  /** Optional second line: what the reader can do about it. */
  hint?: ReactNode;
  action?: ReactNode;
  className?: string;
  /** Fills the height of a chart slot so the layout does not jump. */
  fill?: boolean;
}

/**
 * The one shell every empty and error state uses.
 *
 * Plan FR-DASH-11 is that an empty or failed state must state the reason, and
 * that a blank chart or an unresolving spinner is never acceptable. Funnelling
 * all of them through one component is how that stays true as screens are
 * added: there is no path that renders nothing.
 */
export function StateBlock({
  icon: Icon,
  title,
  detail,
  tone = 'neutral',
  hint,
  action,
  className,
  fill = false,
}: StateBlockProps) {
  const classes = TONE_CLASSES[tone];
  return (
    <div
      role={tone === 'destructive' ? 'alert' : 'status'}
      className={cn(
        'flex flex-col items-center justify-center gap-3 px-6 py-16 text-center',
        fill && 'h-full min-h-[16rem]',
        className,
      )}
    >
      <span
        className={cn(
          'flex size-10 shrink-0 items-center justify-center rounded-2xl',
          classes.ring,
        )}
      >
        <Icon className={cn('size-5', classes.icon)} />
      </span>
      <div className="space-y-1">
        <p className="text-sm font-medium">{title}</p>
        <p className="mx-auto max-w-md text-sm text-muted-foreground">{detail}</p>
        {hint ? <div className="mx-auto max-w-md text-xs text-muted-foreground">{hint}</div> : null}
      </div>
      {action}
    </div>
  );
}
